/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useState } from "react";
import { DataTableWrapper } from "@/Components/datatable/data-table";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/Components/ui/tooltip"

import { FilePlus2 } from "lucide-react";
import { getDecisionColumns, columnDisplayNames } from "@/Components/Decisions/decisions_table/columns";
import { Card } from "../Components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/Components/ui/dialog";
import React from "react";
import { AppContext } from "@/App";
import Confirmation from "@/hooks/confirmation";
import PageLockProvider, { PageLockContext, PageName } from "@/Components/PageLockProvider";
import { formatDate } from "date-fns";
import HelpLink from "@/Components/ui/help-link";
import CSV_Importer from "@/Components/datatable/data_importer/csv_importer";
import { DeleteIcon, EditIcon } from "@/Components/ui/Icons/TooltipIcon";
import DecisionPopup from "@/Components/Decisions/decisions_table/DecisionPopup";
import DecisionForm from "@/Components/Decisions/decisions_table/DecisionForm";
import WizardProvider, { WizardContext } from "@/Components/wizard/wizardProvider";
import { getBasicPageTourViewOnlySteps, getBasicTourSteps } from "@/Components/Decisions/decisions_wizards/basic_page_tour";
import { DecisionContext, Decision } from "@/Components/Decisions/Decision";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";
import { useIsMobile } from "@/hooks/use-mobile";
import TutorialChecker from "@/Components/Tutorial/TutorialChecker";
import { Tutorial } from "@/lib/utils";

export type DecisionColumnVisibility = {
    decisionId: boolean
    date: boolean
    workstream: boolean
    workPackage: boolean
    description: boolean
    raisedBy: boolean
    decider: boolean
    dateMade: boolean
    status: boolean
    notes: boolean
    seen: boolean
    acknowledged: boolean
}

function DecisionsPage() {
    const app = useContext(AppContext);
    const lock = useContext(PageLockContext);
    const wizard = useContext(WizardContext);
    const zoomLevel = useResponsiveZoom();
    const isMobile = useIsMobile();

    const [demoMode, setDemoMode] = useState<boolean>(false);
    const [dialogOpen, setDialogOpen] = React.useState(false);

    const useInlineEditing = app.applicationUser?.userSettings?.inlineTableEditing ?? false;

    const [loading, setLoading] = React.useState(true);
    const [decisionTableContent, setDecisionTableContent] = useState<Decision[]>([]);
    const [decisionTableDemoContent, setDecisionTableDemoContent] = useState<Decision[]>([]);

    const [rowSelection, setRowSelection] = useState({});

    const [dialogTitle, setDialogTitle] = useState("Create New Decision");
    const [dialogTitleBarActions, setDialogTitleBarActions] = useState<JSX.Element | null>(null);
    const [dialogContent, setDialogContent] = useState<JSX.Element | null>(null);

    const users = app.teamMembers.concat(app.projectManagers).concat(app.sponsors);
    const [importedRows, setImportedRows] = useState<any[]>([]);

    React.useEffect(() => {
        if (importedRows.length > 0) {
            const newDecisions = importedRows.map((row) => {
                const newDecision: Decision = {
                    decisionId: 0,
                    date: row.date,
                    workstream: row.workstream,
                    workPackage: row.workPackage,
                    description: row.description,
                    raisedBy: row.raisedBy,
                    decider: row.decider,
                    dateMade: row.dateMade,
                    status: row.status,
                    notes: row.notes,
                    projectId: app.selectedProject?.id || 0,
                    workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
                    workPackageId: app.workPackageOptions?.flatMap(w => w.workPackageNames).find(wp => wp.workPackageName === row.workPackage)?.workPackageId || 0,
                    userId: String(users.find(u => u.displayName === row.decider)?.id ?? ""),
                    seen: false,
                    acknowledged: false
                };
                return newDecision;
            });
            saveDecisions(newDecisions);
            setImportedRows([]);
        }
    }, [importedRows]);

    const importDataType: { name: string, type: any, example: string }[] = [
        { "name": "date", "type": Date, example: "2026-01-23" },
        { "name": "workPackage", "type": String, example: "WP-1" },
        { "name": "description", "type": String, example: "Decision description" },
        { "name": "decider", "type": String, example: "John Doe" },
        { "name": "dateMade", "type": Date, example: "2026-01-27" },
        { "name": "status", "type": String, example: "Open" },
        { "name": "notes", "type": String, example: "Additional notes" }
    ];

    React.useEffect(() => {
        populateDemoData();
        if (app.authLevel !== 3) { // viewer cant see this page. Sponsor can see it but not edit it
            const down = (e: KeyboardEvent) => {
                if (e.key === "r" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault()
                    addDecision();
                }
            }
            document.addEventListener("keydown", down)
            return () => document.removeEventListener("keydown", down)
        }
    }, []);

    React.useEffect(() => {
        refreshDecisions();
    }, [app.selectedProject]);

    const prevDecisionTableContentRef = React.useRef<Decision[]>([]);

    React.useEffect(() => {
        // Skip on initial load or when refreshing all risks
        if (prevDecisionTableContentRef.current.length === 0 ||
            loading ||
            decisionTableContent.length !== prevDecisionTableContentRef.current.length) {
            prevDecisionTableContentRef.current = [...decisionTableContent];
            return;
        }

        // Find updated risks by comparing with previous state
        const updatedDecisions = decisionTableContent.filter((currentDecision) => {
            const prevDecision = prevDecisionTableContentRef.current.find(a => a.decisionId === currentDecision.decisionId);
            if (!prevDecision) return false;

            // Compare all properties to detect changes
            return JSON.stringify(prevDecision) !== JSON.stringify(currentDecision);
        });

        // Save each updated risk to the server
        updatedDecisions.forEach(decision => {
            if (decision.decisionId > 0) { // Only update existing risks, not new ones
                updateDecision(decision);
            }
        });

        // Update ref with current state
        prevDecisionTableContentRef.current = [...decisionTableContent];
    }, [decisionTableContent]);

    function showFormDialog(selectedDecision?: Decision) {
        const action = selectedDecision ? (
            <div onClick={(e) => {
                e.stopPropagation();
                if (selectedDecision.decisionId) {
                    confirmDeleteDecisions([selectedDecision.decisionId]);
                }
            }}
                className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
            </div>
        ) : null;

        setDialogTitleBarActions(action);

        setDialogTitle(selectedDecision ? "Edit Decision" : "Create New Decision");

        setDialogContent(
            <DecisionForm
                saveDecision={saveDecision}
                decision={selectedDecision}
            />
        );
        setDialogOpen(true);
    }

    function showPopup(selectedDecision: Decision) {
        const actions = (
            <div className="flex flex-row">
                <div onClick={() => {
                    showFormDialog(selectedDecision);
                }}
                    className="ml-2 p-2 rounded-md cursor-pointer hover:bg-blue-600"
                >
                    <EditIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
                <div onClick={(e) => {
                    e.stopPropagation();
                    if (selectedDecision.decisionId) {
                        confirmDeleteDecisions([selectedDecision.decisionId]);
                    }
                }}
                    className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                    <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
            </div>
        );

        setDialogTitle("Decision Details");
        setDialogTitleBarActions(
            app.authLevel !== 3 && !lock.viewOnly ? actions : null
        );
        setDialogContent(
            <DecisionPopup
                decision={selectedDecision}
            />
        );
        setDialogOpen(true);
    }

    function refreshDecisions() {
        if (app.selectedProject) {
            setLoading(true);
            document.body.style.cursor = 'wait';
            // fetch decisions for project
            fetch("Raids/Decisions/" + app.selectedProject.id)
                .then((response) => response.json())
                .then((data) => {
                    setDecisionTableContent(data);
                    setLoading(false);
                    document.body.style.cursor = 'default';
                }
                );
        }
    }

    function confirmDeleteDecisions(decisionIds: number[]) {
        setDialogTitleBarActions(null);
        setDialogTitle(decisionIds.length > 1 ? "Are you sure you want to delete these decision?" : "Are you sure you want to delete this decision?");
        setDialogContent(<Confirmation onConfirm={() => { deleteDecisions(decisionIds); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
        setDialogOpen(true);
    }

    function editDecision(id: number) {
        if (demoMode) {
            showFormDialog(decisionTableDemoContent.find((d) => d.decisionId === id));
            return;
        }
        showFormDialog(decisionTableContent.find((d) => d.decisionId === id));
    }

    function loadColumnVisibility(): DecisionColumnVisibility {
        const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_Decisions_columnVisibility");
        if (localStorageValue) {
            const visibiltyValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                decisionId: visibiltyValues.find((v) => v.columnId === "decisionId")?.isVisible ?? false,
                date: visibiltyValues.find((v) => v.columnId === "date")?.isVisible ?? true,
                workstream: visibiltyValues.find((v) => v.columnId === "workstream")?.isVisible ?? false,
                workPackage: visibiltyValues.find((v) => v.columnId === "workPackage")?.isVisible ?? true,
                description: visibiltyValues.find((v) => v.columnId === "description")?.isVisible ?? true,
                raisedBy: visibiltyValues.find((v) => v.columnId === "raisedBy")?.isVisible ?? true,
                decider: visibiltyValues.find((v) => v.columnId === "decider")?.isVisible ?? true,
                dateMade: visibiltyValues.find((v) => v.columnId === "dateMade")?.isVisible ?? true,
                status: visibiltyValues.find((v) => v.columnId === "status")?.isVisible ?? true,
                notes: visibiltyValues.find((v) => v.columnId === "notes")?.isVisible ?? false,
                seen: visibiltyValues.find((v) => v.columnId === "seen")?.isVisible ?? false,
                acknowledged: visibiltyValues.find((v) => v.columnId === "acknowledged")?.isVisible ?? false,
            })
        }
        return {
            decisionId: false,
            date: true,
            workstream: false,
            workPackage: true,
            description: true,
            raisedBy: true,
            decider: true,
            dateMade: true,
            status: true,
            notes: false,
            seen: false,
            acknowledged: false,
        }
    }

    function addDecision() {
        if (useInlineEditing) {
            const newDecision: Decision = {
                decisionId: 0,
                date: new Date(),
                projectId: app.selectedProject?.id || 0,
                workstream: app.workPackageOptions?.[0]?.workstreamName || "",
                workPackage: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageName || "",
                workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0, // default to first workstream
                workPackageId: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageId || 0, // default to first work package
                description: "New Decision",
                raisedBy: "",
                decider: "",
                userId: "",
                dateMade: new Date(),
                status: "Open",
                notes: "",
                seen: false,
                acknowledged: false,
            };

            saveDecision(newDecision);
        } else {
            showFormDialog();
        }
    }

    function saveDecision(Decision: Decision) {
        if (Decision.decisionId) {
            updateDecision(Decision);
            return;
        }
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newDecision = { ...Decision, decisionId: Math.floor(Math.random() * 1000000) };
            setDecisionTableDemoContent([newDecision, ...decisionTableDemoContent]);
            app.showActionNotification("Success", "Decision Added Successfully!", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/Decision", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(Decision),
        })
            .then((response) => response.json())
            .then((data) => {
                setDecisionTableContent((prev) => [data, ...prev]);
                app.showActionNotification("Success", "Decision Added Successfully!", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding decision: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function saveDecisions(decisions: Decision[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newDecisions = decisions.map(decision => ({ ...decision, decisionId: Math.floor(Math.random() * 1000000) }));
            setDecisionTableDemoContent([...newDecisions, ...decisionTableDemoContent]);
            app.showActionNotification("Success", `${decisions.length} Decisions added from CSV`, "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/Decisions", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(decisions),
        })
            .then((response) => response.json())
            .then((data) => {
                setDecisionTableContent((prev) => [...data, ...prev]);
                app.showActionNotification("Success", "Decisions Added Successfully!", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding decisions: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function updateDecision(decision: Decision) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newDecision = { ...decision, decisionId: Math.floor(Math.random() * 1000000) };
            setDecisionTableDemoContent((prev) =>
                prev.map((d) => (d.decisionId === decision.decisionId ? newDecision : d))
            );
            app.showActionNotification("Success", "Decision Updated Successfully!", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch(`/Raids/Decision/${decision.decisionId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...decision,
                date: decision.date ? formatDate(decision.date, "yyyy-MM-dd") : undefined,
                dateMade: decision.dateMade ? formatDate(decision.dateMade, "yyyy-MM-dd") : undefined,
            }),
        })
            .then((response) => {
                if (response.ok) {
                    if (!useInlineEditing) {
                        // If not using inline editing, update the specific action in state
                        setDecisionTableContent((prev) =>
                            prev.map((d) => (d.decisionId === decision.decisionId ? decision : d))
                        );
                    }
                    app.showActionNotification("Success", "Decision Updated Successfully!", "success");
                } else {
                    app.showActionNotification("Error", "Error updating decision: " + response.statusText, "destructive");
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error updating decision: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function deleteDecisions(ids: number[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            setDecisionTableDemoContent((prev) => prev.filter((d) => !ids.includes(d.decisionId)));
            app.showActionNotification("Success", "Decision Deleted", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/Decisions", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(ids),
        })
            .then(() => {
                setDecisionTableContent(decisionTableContent.filter((d) => !ids.includes(d.decisionId)));
                app.showActionNotification("Success", "Decision Deleted", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error deleting decision: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function getGroupActions() {
        const groupActions = app.authLevel > 3 ? [{
            name: "Delete",
            action: (selectedRows: any[]) => {
                confirmDeleteDecisions(selectedRows.map((row) => row.decisionId));
            },
        }] : [];

        groupActions.push(
            {
                name: "Set Status to Open",
                action: (selectedRows: any[]) => {
                    const decisionStatus = selectedRows.map((row) => ({ "id": row.decisionId, "status": "Open" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(decisionStatus);
                },
            },
            {
                name: "Set Status to Closed",
                action: (selectedRows: any[]) => {
                    const decisionStatus = selectedRows.map((row) => ({ "id": row.decisionId, "status": "Closed" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(decisionStatus);
                },
            },
            {
                name: "Set Status to Cancelled",
                action: (selectedRows: any[]) => {
                    const decisionStatus = selectedRows.map((row) => ({ "id": row.decisionId, "status": "Cancelled" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(decisionStatus);
                },
            });
        return groupActions;
    }

    function setStatus(decisionStatus: { id: number, status: "Closed" | "Cancelled" | "Open" }[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            setDecisionTableDemoContent(decisionTableDemoContent.map((decision) => {
                const updatedDecision = decisionStatus.find((ds) => ds.id === decision.decisionId);
                if (updatedDecision) {
                    decision.status = updatedDecision.status;
                }
                return decision;
            }));
            app.showActionNotification("Success", "Status Updated", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/DecisionStatus", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(decisionStatus),
        })
            .then(() => {
                setDecisionTableContent(decisionTableContent.map((decision) => {
                    const updatedDecision = decisionStatus.find((is) => is.id === decision.decisionId);
                    if (updatedDecision) {
                        decision.status = updatedDecision.status;
                    }
                    return decision;
                }));
                app.showActionNotification("Success", "Status Updated", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error updating status: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function populateDemoData() {
        const demoDecisions: Decision[] = [];
        const deciders = users.length > 0 ? users.map(u => u.displayName) : ["User 1", "User 2", "User 3"];
        const workPackages = app.workPackageOptions?.flatMap(w => w.workPackageNames).map(wp => wp.workPackageName) || ["WP-1", "WP-2", "WP-3"];
        for (let i = 0; i < 20; i++) {
            const randomDecider = deciders[Math.floor(Math.random() * deciders.length)];
            const randomRaisedBy = deciders[Math.floor(Math.random() * deciders.length)];
            const randomWorkPackage = workPackages[Math.floor(Math.random() * workPackages.length)];
            const newDecision: Decision = {
                decisionId: i + 1,
                date: new Date(new Date().setDate(new Date().getDate() - Math.floor(Math.random() * 30))),
                projectId: app.selectedProject?.id || 0,
                workstream: app.workPackageOptions?.[0]?.workstreamName || "Default Stream",
                workPackage: randomWorkPackage,
                workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
                workPackageId: app.workPackageOptions?.flatMap(w => w.workPackageNames).find(wp => wp.workPackageName === randomWorkPackage)?.workPackageId || 0,
                raisedBy: randomRaisedBy,
                description: `This is a demo decision number ${i + 1}`,
                decider: randomDecider,
                userId: users.find(u => u.displayName === randomDecider)?.id?.toString() || "",
                dateMade: new Date(new Date().setDate(new Date().getDate() - Math.floor(Math.random() * 20))),
                status: Math.random() < 0.7 ? "Open" : (Math.random() < 0.5 ? "Closed" : "Cancelled"),
                notes: Math.random() < 0.5 ? `These are some notes for decision number ${i + 1}` : "",
                seen: Math.random() < 0.5,
                acknowledged: Math.random() < 0.5,
            };
            demoDecisions.push(newDecision);
        }
        setDecisionTableDemoContent(demoDecisions);
    }

    const demoButton = (
        <TooltipProvider delayDuration={1}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button id="exitDemoMode" className="rounded-md text-white bg-red-500 h-min px-2 my-auto" onClick={() => setDemoMode(false)}>Exit Demo Mode</button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-col bg-background">
                    <div className="flex flex-row text-muted-foreground">
                        <p>
                            Demo mode allows you to try out the Decisions page without making any actual changes to the data.
                        </p>
                        <p>
                            The table will be populated with some sample data and you can try out adding, editing, deleting and exporting Decisions.
                        </p>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    const contextValue = {
        dialogOpen,
        setDialogOpen,
        decisionTableDemoContent,
        setDecisionTableDemoContent,
        showPopup,
        demoMode,
        setDemoMode,
        rowSelection,
        setRowSelection,
        addDecision,
    };

    if (isMobile) {
        return (
            <DecisionContext.Provider value={contextValue}>
                <Card className="mb-8 relative" style={{ zoom: zoomLevel }}>
                    {wizard && wizard.getWizardPointer()}
                    <div className="flex flex-row bg-table-decisions justify-between px-2 rounded-t-lg">
                        <div className="flex flex-row">
                            <h1 className="text-4xl my-auto p-2">Decisions</h1>
                            <div className="my-auto" onClick={refreshDecisions}>{lock.getRefreshControl()}</div>
                        </div>
                        <div className="flex flex-row">
                            {app.authLevel !== 3 && !lock.viewOnly ? (
                                <div className="flex flex-row justify-end">
                                    <TooltipProvider delayDuration={1}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div
                                                    id="addNewDecision"
                                                    onClick={() => addDecision()}
                                                    className="rounded-full bg-table-decisions border border-white text-table-header-foreground p-2 hover:cursor-pointer m-2"
                                                >
                                                    <FilePlus2 className="w-8 h-8" />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent className="flex flex-col bg-background">
                                                <div className="flex flex-row text-muted-foreground place-content-between">
                                                    <span className="p-2">Create New Decision</span>
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            ) : <div></div>}
                        </div>
                        <div className="flex flex-row absolute top-0 right-2 -translate-y-[130%]">{app.authLevel !== 3 && lock.getLockControl()}</div>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogContent id="decision-dialog" className="flex flex-col w-[95%]" style={{ zoom: zoomLevel }} onInteractOutside={(e) => {
                            if (demoMode) {
                                e.preventDefault();
                            }
                        }}>
                            <DialogHeader>
                                <DialogTitle><div className="flex"><div className="text-4xl my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                            </DialogHeader>
                            {dialogContent}
                        </DialogContent>
                    </Dialog>
                    <DataTableWrapper
                        columns={getDecisionColumns(
                            (ids: number[]) => confirmDeleteDecisions(ids),
                            (id: number) => editDecision(id),
                        )}
                        data={demoMode ? decisionTableDemoContent : decisionTableContent}
                        setData={demoMode ? setDecisionTableDemoContent : setDecisionTableContent}
                        loading={loading}
                        viewOnly={app.authLevel === 3 || lock.viewOnly}
                        header_colour="bg-table-decisions"
                        table_title="Decisions"
                        columnVisibility={loadColumnVisibility()}
                        groupActions={getGroupActions()}
                        columnDisplayNames={columnDisplayNames}
                        onRowClick={!(useInlineEditing) ? (row) => showPopup(row.original as Decision) : undefined}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                    />
                </Card>
            </DecisionContext.Provider>
        );
    }

    return (
        <DecisionContext.Provider value={contextValue}>
            <Card className="p-2 m-2 relative" style={{ zoom: zoomLevel }}>
                {wizard && wizard.getWizardPointer()}
                <div className="flex flex-row justify-between">
                <div className="flex flex-row"><h1 className="text-2xl my-auto p-2">Decisions</h1><div className="my-auto" onClick={refreshDecisions}>{lock.getRefreshControl()}</div></div>
                <div className="flex flex-row">{app.authLevel !== 3 && lock.getLockControl()}<HelpLink page="decision" />{demoMode && demoButton}</div>
                {app.authLevel !== 3 && !lock.viewOnly ? ( // viewer cant see this page. Sponsor can see it but not edit it
                    <div className="flex flex-row justify-end">
                        <TooltipProvider delayDuration={1}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div
                                        id="addNewDecision"
                                        onClick={() => addDecision()}
                                        className="rounded-full bg-table-decisions text-table-header-foreground w-min p-2 hover:cursor-pointer m-2"
                                    >
                                        <FilePlus2 />
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent className="flex flex-col bg-background">
                                    <div className="flex flex-row text-muted-foreground place-content-between">
                                        <span className="p-2">Create New Decision</span>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        {<CSV_Importer propertyTypes={importDataType} setNewRows={setImportedRows} bgColor="bg-table-decisions" title="Import Decisions from CSV" />}
                    </div>
                ) : <div></div>}
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent id="decision-dialog" onInteractOutside={(e) => {
                    if (demoMode) {
                        e.preventDefault();
                    }
                }}>
                    <DialogHeader>
                        <DialogTitle><div className="flex"><div className="my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                    </DialogHeader>
                    {dialogContent}
                </DialogContent>
            </Dialog>
            <TutorialChecker 
                    tutorialId={Tutorial.RAIDS} 
                    tutorial={async () => { 
                            setDemoMode(true); 
                            const acquiredLock = await lock.lockPage();
                            wizard.setWizardSteps(getBasicPageTourViewOnlySteps({ viewOnly: false, decisionContext: contextValue })); 
                            wizard.setGuidedTourCompleteFunctionCall(() => () =>  { lock.unlockPage(acquiredLock?.id); setDemoMode(false); });
                            wizard.startGuidedTour(); }} />
            <DataTableWrapper
                columns={getDecisionColumns(
                    (ids: number[]) => confirmDeleteDecisions(ids),
                    (id: number) => editDecision(id),
                )}
                data={demoMode ? decisionTableDemoContent : decisionTableContent}
                setData={demoMode ? setDecisionTableDemoContent : setDecisionTableContent}
                loading={loading}
                viewOnly={app.authLevel === 3 || lock.viewOnly} // viewer can see but not edit
                header_colour="bg-table-decisions"
                table_title="Decisions"
                columnVisibility={loadColumnVisibility()}
                groupActions={getGroupActions()}
                columnDisplayNames={columnDisplayNames}
                onRowClick={!(useInlineEditing) ? (row) => showPopup(row.original as Decision) : undefined}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
            />
                <div className="flex flex-row w-min ml-auto">{wizard.getTourControls()}</div>
            </Card>
        </DecisionContext.Provider>
    );
}

function Decisions() {
    const app = useContext(AppContext);
    return <PageLockProvider pageName={PageName.Decisions} userId={app.applicationUser?.id as string} projectId={app.selectedProject?.id as number} >
        <WizardProvider wizardSteps={getBasicTourSteps()} >
            <DecisionsPage />
        </WizardProvider>
    </PageLockProvider>;
}

export default Decisions;